import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface ScoreHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    rounds: Array<{
        teamA: number;
        teamB: number;
    }>;
    currentRound: number;
}

const ScoreHistoryModal: React.FC<ScoreHistoryModalProps> = ({
    isOpen,
    onClose,
    rounds,
    currentRound
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    // Calculate cumulative scores
    const cumulativeScores = rounds.reduce((acc, round, index) => {
        const prev = acc[index - 1] || { teamA: 0, teamB: 0 };
        acc.push({
            teamA: prev.teamA + round.teamA,
            teamB: prev.teamB + round.teamB,
            roundScoreA: round.teamA,
            roundScoreB: round.teamB,
            diff: Math.abs((prev.teamA + round.teamA) - (prev.teamB + round.teamB))
        });
        return acc;
    }, [] as Array<{
        teamA: number;
        teamB: number;
        roundScoreA: number;
        roundScoreB: number;
        diff: number;
    }>);

    const bestRoundA = cumulativeScores.length > 0 ? Math.max(...cumulativeScores.map(r => r.roundScoreA)) : 0;
    const bestRoundB = cumulativeScores.length > 0 ? Math.max(...cumulativeScores.map(r => r.roundScoreB)) : 0;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 100,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 'var(--space-lg)'
                        }}
                    >
                        {/* Modal */}
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-card"
                            style={{
                                maxWidth: '600px',
                                width: '100%',
                                maxHeight: '80vh',
                                overflow: 'auto',
                                padding: 'var(--space-xl)'
                            }}
                        >
                            {/* Header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--space-lg)',
                                borderBottom: '2px solid var(--border-color)',
                                paddingBottom: 'var(--space-md)'
                            }}>
                                <h2 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: 'var(--text-primary)',
                                    margin: 0
                                }}>
                                    📊 {t('scores.history')}
                                </h2>
                                <button
                                    onClick={onClose}
                                    className="btn btn-secondary"
                                    style={{
                                        padding: 'var(--space-sm)',
                                        minWidth: 'auto',
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Table */}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.875rem'
                                }}>
                                    <thead>
                                        <tr style={{
                                            background: 'var(--bg-card)',
                                            borderBottom: '2px solid var(--border-color-light)'
                                        }}>
                                            <th style={{
                                                padding: 'var(--space-md)',
                                                textAlign: 'center',
                                                color: 'var(--text-secondary)',
                                                fontWeight: '600'
                                            }}>
                                                {t('round')}
                                            </th>
                                            <th style={{
                                                padding: 'var(--space-md)',
                                                textAlign: 'center',
                                                color: 'var(--team-a)',
                                                fontWeight: '700'
                                            }}>
                                                🔵 {t('teamA')}
                                            </th>
                                            <th style={{
                                                padding: 'var(--space-md)',
                                                textAlign: 'center',
                                                color: 'var(--team-b)',
                                                fontWeight: '700'
                                            }}>
                                                🔴 {t('teamB')}
                                            </th>
                                            <th style={{
                                                padding: 'var(--space-md)',
                                                textAlign: 'center',
                                                color: 'var(--text-secondary)',
                                                fontWeight: '600'
                                            }}>
                                                {t('scores.gap')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cumulativeScores.map((score, index) => {
                                            const isLeaderA = score.teamA > score.teamB;
                                            const roundNumber = index + 1;
                                            const isCurrent = roundNumber === currentRound;

                                            return (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    style={{
                                                        borderBottom: '1px solid var(--border-color)',
                                                        background: isCurrent ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                                        fontWeight: isCurrent ? '700' : '400'
                                                    }}
                                                >
                                                    <td style={{
                                                        padding: 'var(--space-md)',
                                                        textAlign: 'center',
                                                        color: isCurrent ? 'var(--team-a)' : 'var(--text-secondary)'
                                                    }}>
                                                        {isCurrent && '▶ '}{roundNumber}
                                                    </td>
                                                    <td style={{
                                                        padding: 'var(--space-md)',
                                                        textAlign: 'center'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '2px'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '1rem',
                                                                color: 'var(--team-a)',
                                                                fontWeight: '700'
                                                            }}>
                                                                {Math.round(score.teamA)}
                                                                {isLeaderA && ' 👑'}
                                                            </span>
                                                            <span style={{
                                                                fontSize: '0.7rem',
                                                                color: 'var(--text-muted)'
                                                            }}>
                                                                +{Math.round(score.roundScoreA)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{
                                                        padding: 'var(--space-md)',
                                                        textAlign: 'center'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '2px'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '1rem',
                                                                color: 'var(--team-b)',
                                                                fontWeight: '700'
                                                            }}>
                                                                {Math.round(score.teamB)}
                                                                {!isLeaderA && score.teamB > score.teamA && ' 👑'}
                                                            </span>
                                                            <span style={{
                                                                fontSize: '0.7rem',
                                                                color: 'var(--text-muted)'
                                                            }}>
                                                                +{Math.round(score.roundScoreB)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{
                                                        padding: 'var(--space-md)',
                                                        textAlign: 'center',
                                                        color: 'var(--text-secondary)'
                                                    }}>
                                                        {score.diff > 0 ? `±${Math.round(score.diff)}` : '-'}
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer Summary */}
                            <div style={{
                                marginTop: 'var(--space-lg)',
                                padding: 'var(--space-md)',
                                background: 'var(--bg-card)',
                                borderRadius: 'var(--radius-md)',
                                display: 'flex',
                                justifyContent: 'space-around',
                                fontSize: '0.875rem'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                        {t('scores.roundsPlayed')}
                                    </div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                                        {cumulativeScores.length}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                        {t('scores.bestRound')} A
                                    </div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--team-a)' }}>
                                        {bestRoundA}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                        {t('scores.bestRound')} B
                                    </div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--team-b)' }}>
                                        {bestRoundB}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ScoreHistoryModal;
